import { useMemo, useState } from "react";
import useSWR from "swr";
import {
  groupByDay,
  initialDay as pickInitialDay,
  initials,
  isWorkshop,
  pickQuickBookCredit,
  QuickBookDialog,
  scheduleWeek,
  ScheduleView,
  spotsState,
  useBooking,
  type ClassAction,
  type ScheduleNotice,
  type ScheduleSession,
} from "@studiolite/booking";
import { swrFetcher } from "@/lib/api";
import { track } from "@/lib/analytics";
import { classHref, weekHref } from "@/lib/widget-hrefs";
import { bookWithCredit } from "@/lib/booking-api";
import { useStudentCredits } from "@/hooks/use-student-credits";
import { withLocale } from "@/lib/widget-locale";
import { useWaitlistJoin } from "@/hooks/use-waitlist-join";
import { useAuthStore } from "@/store/auth-store";
import { useScheduleStore } from "@/store/schedule-store";
import { useStudioStore } from "@/store/studio-store";
import { useViewStore } from "@/store/view-store";
import type { ClassSession } from "@/types/ClassSession";
import { useTranslations } from "../../../i18n";
import Loader from "../loader";

/**
 * Planning du widget : les memes donnees (GET /public/studios/:slug/planning)
 * et le meme ScheduleView que le site du studio. Grille de la semaine quand
 * l'iframe est large, bandeau des jours et liste quand elle est etroite.
 */
export function CalendarPage() {
  const studio = useStudioStore((s) => s.studio);
  if (!studio) return null;
  return <WidgetSchedule studioId={studio.id} slug={studio.slug} timeZone={studio.timezone} />;
}

function WidgetSchedule({
  studioId,
  slug,
  timeZone,
}: {
  studioId: string;
  slug: string;
  timeZone: string;
}) {
  const t = useTranslations("calendar");
  const { t: tb } = useBooking();
  const requestedWeek = useScheduleStore((s) => s.week);
  const storedDay = useScheduleStore((s) => s.day);
  const setDay = useScheduleStore((s) => s.setDay);

  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const setSelectedEvent = useViewStore((s) => s.setSelectedEvent);
  const onBookingSuccess = useViewStore((s) => s.onBookingSuccess);
  const { joinWaitlist, isJoining } = useWaitlistJoin();
  const { credits, refresh: refreshCredits } = useStudentCredits();

  // Reservation en un geste : l'eleve a deja un credit valable pour ce cours.
  const [quickBook, setQuickBook] = useState<ScheduleSession | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [quickError, setQuickError] = useState<string | null>(null);
  // « A propos du cours », charge a l'ouverture de la fenetre.
  const { data: details } = useSWR<{
    classSession: { description: string | null; tags: string[] | null } | null;
  }>(
    quickBook ? `/api/widget/public/site/${studioId}/class-sessions/${quickBook.id}` : null,
    swrFetcher,
  );
  const quick = quickBook ? pickQuickBookCredit(credits, quickBook.startTime) : null;

  // data-layout="agenda" : la liste a toutes les largeurs, comme avant.
  const layout = useMemo(
    () => new URLSearchParams(window.location.search).get("layout"),
    [],
  );

  const week = useMemo(() => scheduleWeek(requestedWeek, timeZone), [requestedWeek, timeZone]);
  const range = `from=${week.from.toISOString()}&to=${week.to.toISOString()}`;

  // past=1 : les cours deja passes de la semaine, affiches en grise.
  // La reponse garde sa semaine, et keepPreviousData laisse la semaine
  // affichee a l'ecran jusqu'a l'arrivee de la suivante : pas de saut de
  // hauteur sur la page hote a chaque changement de semaine.
  const {
    data: shown,
    error,
    isLoading,
    mutate,
  } = useSWR(
    withLocale(`/api/public/studios/${encodeURIComponent(slug)}/planning?${range}&past=1`),
    async (url: string) => ({
      week,
      sessions: (await swrFetcher(url)) as ScheduleSession[],
    }),
    { keepPreviousData: true },
  );
  // Bandeaux de fermeture, en haut du planning.
  const { data: notices } = useSWR<ScheduleNotice[]>(
    `/api/public/studios/${encodeURIComponent(slug)}/notices`,
    swrFetcher,
  );
  const { data: bookedIds, mutate: refreshBooked } = useSWR<string[]>(
    isAuthenticated ? `/api/widget/profile/bookings/session-ids?${range}` : null,
    swrFetcher,
  );

  if (!shown) {
    return error ? <LoadError onRetry={() => mutate()} /> : <Loader />;
  }

  const openClass = (session: ScheduleSession) => {
    track("class_viewed", {
      class_session_id: session.id,
      class_type: session.classType?.name ?? null,
      source: "calendar",
    });
    // L'ecran de reservation recharge la seance a partir de son id.
    setSelectedEvent({ id: session.id } as ClassSession, isAuthenticated);
  };

  const getAction = (session: ScheduleSession): ClassAction => {
    if (!session.bookable) return { kind: "none" };
    if (bookedIds?.includes(session.id)) return { kind: "booked" };

    const state = spotsState(session);
    if (state.kind === "full") {
      if (!state.canJoinWaitlist) return { kind: "none" };
      return {
        kind: "waitlist",
        pending: isJoining,
        onJoin: () =>
          isAuthenticated
            ? void joinWaitlist(session.id)
            : setSelectedEvent({ id: session.id } as ClassSession, false),
      };
    }

    // Un credit valable : la fenetre de reservation en un geste. Les ateliers
    // passent toujours par le paiement (options et prix propres).
    const canQuickBook =
      isAuthenticated && !isWorkshop(session) && !!pickQuickBookCredit(credits, session.startTime);
    return {
      kind: "book",
      href: classHref(session.id),
      onBook: () => (canQuickBook ? openQuickBook(session) : openClass(session)),
    };
  };

  const openQuickBook = (session: ScheduleSession) => {
    setQuickError(null);
    setQuickBook(session);
    track("class_viewed", {
      class_session_id: session.id,
      class_type: session.classType?.name ?? null,
      source: "quickbook",
    });
  };

  const confirmQuickBook = async () => {
    if (!quickBook || !quick) return;
    setConfirming(true);
    setQuickError(null);
    try {
      const booked = await bookWithCredit(quickBook.id, quick.credit);
      track("quickbook_confirmed", {
        class_session_id: quickBook.id,
        pass_type: `existing_${quick.credit.kind}`,
      });
      setQuickBook(null);
      void Promise.all([refreshBooked(), refreshCredits()]);
      onBookingSuccess(booked.bookingId);
    } catch (error) {
      console.error("Quick booking failed:", error);
      setQuickError(tb("checkout.bookingError"));
    } finally {
      setConfirming(false);
    }
  };

  const firstDay = pickInitialDay(
    shown.week,
    groupByDay(shown.sessions, timeZone),
    timeZone,
    storedDay,
  );

  return (
    <div
      aria-busy={isLoading || undefined}
      className={`p-4 md:p-5 ${isLoading ? "opacity-70 transition-opacity" : ""}`}
    >
      <ScheduleView
        key={shown.week.start}
        sessions={shown.sessions}
        week={shown.week}
        previousHref={weekHref(shown.week.previous)}
        nextHref={weekHref(shown.week.next)}
        todayHref={weekHref(null)}
        initialDay={firstDay}
        onDayChange={setDay}
        getAction={getAction}
        account={<AccountButton />}
        wideLayout={layout === "agenda" ? "list" : "week"}
        showClassTypes
        notices={notices ?? []}
      />
      {error && (
        <p role="alert" className="mt-3 text-sm text-danger">
          {t("loadError")}
        </p>
      )}
      <QuickBookDialog
        session={quickBook}
        credit={quick?.credit ?? null}
        creditsAfter={quick?.creditsAfter ?? null}
        description={details?.classSession?.description}
        tags={details?.classSession?.tags}
        otherWayHref={quickBook ? classHref(quickBook.id) : "#view=calendar"}
        confirming={confirming}
        error={quickError}
        onConfirm={confirmQuickBook}
        onClose={() => setQuickBook(null)}
      />
    </div>
  );
}

/** « Se connecter », ou « Mon espace » une fois connecte. Plus d'« Inscription » : le compte se cree au paiement. */
function AccountButton() {
  const t = useTranslations("common");
  const user = useAuthStore((s) => s.user);
  const navigate = useViewStore((s) => s.navigate);

  const className =
    "inline-flex h-11 items-center gap-2 rounded-pill px-3 text-sm font-semibold text-ink transition-colors duration-150 hover:bg-surface-muted md:h-9 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-studio-ink";

  if (!user) {
    return (
      <button type="button" className={className} onClick={() => navigate("login")}>
        {t("signIn")}
      </button>
    );
  }

  return (
    <button type="button" className={className} onClick={() => navigate("profile")}>
      <span
        aria-hidden="true"
        className="flex size-7 items-center justify-center rounded-pill bg-studio-accent-soft text-xs font-semibold text-studio-ink"
      >
        {initials(user.name ?? user.email)}
      </span>
      {t("mySpace")}
    </button>
  );
}

function LoadError({ onRetry }: { onRetry: () => void }) {
  const t = useTranslations("calendar");
  return (
    <div className="flex flex-col items-center gap-3 rounded-card border border-line bg-surface px-4 py-10 text-center">
      <p className="text-sm text-ink-soft">{t("loadError")}</p>
      <button
        type="button"
        onClick={onRetry}
        className="inline-flex h-11 items-center rounded-pill border border-line px-5 text-sm font-semibold text-ink hover:border-line-strong"
      >
        {t("retry")}
      </button>
    </div>
  );
}
